import { create } from 'zustand'
import type { Article, Proposal } from '@/types'

interface ImportResult {
  total: number
  imported: number
  skipped: number
  errors: number
  details: {
    imported: string[]
    skipped: string[]
    errors: Array<{ title: string; error: string }>
  }
}

interface ArticlesStore {
  articles: Article[]
  selectedArticle: Article | null
  proposals: Proposal[]
  isLoading: boolean
  error: string | null

  fetchArticles: () => Promise<void>
  fetchArticle: (id: string) => Promise<void>
  addArticle: (url: string, type: string) => Promise<boolean>
  updateArticle: (id: string, data: Partial<Article>) => Promise<boolean>
  deleteArticle: (id: string) => Promise<boolean>
  triggerAnalysis: (id: string) => Promise<string | null>
  importArticles: (options?: { includeDrafts?: boolean; defaultType?: string }) => Promise<ImportResult | null>
  markArticleCompleted: (id: string, completed: boolean) => Promise<boolean>

  fetchProposals: () => Promise<void>
  fetchProposal: (id: string) => Promise<Proposal | null>
  updateProposal: (id: string, data: Partial<Proposal>) => Promise<boolean>
  applyProposal: (id: string, publishAs: 'draft' | 'publish') => Promise<boolean>
}

export const useArticlesStore = create<ArticlesStore>((set, get) => ({
  articles: [],
  selectedArticle: null,
  proposals: [],
  isLoading: false,
  error: null,

  fetchArticles: async () => {
    set({ isLoading: true, error: null })
    try {
      const response = await fetch('/api/articles')
      if (!response.ok) throw new Error('Failed to fetch articles')
      const data = await response.json() as { articles: Article[] }
      set({ articles: data.articles, isLoading: false })
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false })
    }
  },

  fetchArticle: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = await fetch(`/api/articles/${id}`)
      if (!response.ok) throw new Error('Failed to fetch article')
      const data = await response.json() as { article: Article }
      set({ selectedArticle: data.article, isLoading: false })
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false })
    }
  },

  addArticle: async (url: string, type: string) => {
    try {
      const response = await fetch('/api/articles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, type }),
      })
      if (!response.ok) return false
      await get().fetchArticles()
      return true
    } catch {
      return false
    }
  },

  updateArticle: async (id: string, data: Partial<Article>) => {
    try {
      const response = await fetch(`/api/articles/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (!response.ok) return false
      await get().fetchArticles()
      return true
    } catch {
      return false
    }
  },

  deleteArticle: async (id: string) => {
    try {
      const response = await fetch(`/api/articles/${id}`, {
        method: 'DELETE',
      })
      if (!response.ok) return false
      await get().fetchArticles()
      return true
    } catch {
      return false
    }
  },

  triggerAnalysis: async (id: string) => {
    try {
      const response = await fetch(`/api/articles/${id}/analyze`, {
        method: 'POST',
      })
      if (!response.ok) return null
      const data = await response.json() as { proposalId: string }
      return data.proposalId
    } catch {
      return null
    }
  },

  importArticles: async (options = {}) => {
    try {
      const response = await fetch('/api/articles/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options),
      })
      if (!response.ok) {
        const error = await response.json() as { error: string }
        throw new Error(error.error)
      }
      const result = await response.json() as ImportResult
      await get().fetchArticles()
      return result
    } catch (error) {
      set({ error: (error as Error).message })
      return null
    }
  },

  markArticleCompleted: async (id: string, completed: boolean) => {
    try {
      const response = await fetch(`/api/articles/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          completed_at: completed ? new Date().toISOString() : null
        }),
      })
      if (!response.ok) return false
      await get().fetchArticles()
      return true
    } catch {
      return false
    }
  },

  fetchProposals: async () => {
    set({ isLoading: true, error: null })
    try {
      const response = await fetch('/api/proposals')
      if (!response.ok) throw new Error('Failed to fetch proposals')
      const data = await response.json() as { proposals: Proposal[] }
      set({ proposals: data.proposals, isLoading: false })
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false })
    }
  },

  fetchProposal: async (id: string) => {
    try {
      const response = await fetch(`/api/proposals/${id}`)
      if (!response.ok) return null
      const data = await response.json() as { proposal: Proposal }
      return data.proposal
    } catch {
      return null
    }
  },

  updateProposal: async (id: string, data: Partial<Proposal>) => {
    try {
      const response = await fetch(`/api/proposals/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (!response.ok) return false
      await get().fetchProposals()
      return true
    } catch {
      return false
    }
  },

  applyProposal: async (id: string, publishAs: 'draft' | 'publish') => {
    try {
      const response = await fetch(`/api/proposals/${id}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ publishAs }),
      })
      return response.ok
    } catch {
      return false
    }
  },
}))
